import { useEffect, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import categoryService from "app/common/services/categoryService";
import type { CategoryId } from "app/common/services/categoryService.types";
import { GhostLoader } from "app/components/React/Table/SWReactTableGhostLoader";
import { SWReactTableOptimized } from "app/components/React/Table/SWReactTableOptimized";
import { useDidMountEffect } from "app/custom-hooks/useDidMountEffect";
import { columns } from "app/pages/industry-analysis/overview/pre-defined-industries-benchmarking/table/columns/columns";
import { getColumns } from "app/pages/industry-analysis/overview/pre-defined-industries-benchmarking/table/columns/getColumns";
import { fetchData } from "app/pages/industry-analysis/overview/pre-defined-industries-benchmarking/table/fetchData";
import { getTableData } from "app/pages/industry-analysis/overview/pre-defined-industries-benchmarking/table/getTableData";
import {
    onFavoriteClickHof,
    PREFERENCES_SERVICE_FAVORITES_KEY,
} from "app/pages/industry-analysis/overview/pre-defined-industries-benchmarking/table/onFavoriteClickHof";
import { resolveState } from "app/pages/industry-analysis/overview/pre-defined-industries-benchmarking/table/resolveState";
import { Container } from "app/pages/industry-analysis/overview/pre-defined-industries-benchmarking/table/styled";
import { TableTop } from "app/pages/industry-analysis/overview/pre-defined-industries-benchmarking/table/table-top/TableTop";
import type {
    IFilters,
    IRawData } from "app/pages/industry-analysis/overview/pre-defined-industries-benchmarking/table/types";
import {
    EIndustriesType,
    INITIAL_RAW_DATA_STATE
} from "app/pages/industry-analysis/overview/pre-defined-industries-benchmarking/table/types";
import { PreferencesService } from "app/services/preferences/preferencesService";

import { extractPrimaryCategoryFromParams } from "../../../routes/routeUtils";

const IS_FAVORITES_SESSION_STORAGE_KEY = "isFavoriteIndustriesOnly";

export const PreDefinedIndustriesBenchmarkTable = () => {
    const { params } = useCurrentStateAndParams();
    const { category, comparedDuration } = params;
    const primaryCategory = extractPrimaryCategoryFromParams(category);
    const categoryObject = categoryService.categoryQueryParamToCategoryObject(primaryCategory);
    const [filters, setFilters] = useState<IFilters>({
        search: String(),
        isFavorites: sessionStorage.getItem(IS_FAVORITES_SESSION_STORAGE_KEY) === true.toString(),
        sort: {
            field: undefined,
            sortDirection: undefined,
        },
        selectedIndustry: categoryObject,
        industriesType: EIndustriesType.ALL,
    });
    const [rawData, setRawData] = useState<IRawData>(INITIAL_RAW_DATA_STATE);
    const [favorites, setFavorites] = useState<CategoryId[]>(
        PreferencesService.get(PREFERENCES_SERVICE_FAVORITES_KEY) ?? [],
    );
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { search, sort, isFavorites, selectedIndustry, industriesType } = filters;
    const state = resolveState(params);
    const tableColumns = useMemo(() => getColumns(sort, state), [sort, state]);
    useDidMountEffect(() => {
        setFilters({ ...filters, selectedIndustry: categoryObject });
    }, [categoryObject.id, categoryObject.categoryHash]);
    useEffect(() => {
        setIsLoading(true);
        fetchData()
            .then(setRawData)
            .catch(() => setRawData(INITIAL_RAW_DATA_STATE))
            .finally(() => setIsLoading(false));
    }, [comparedDuration]);
    useDidMountEffect(() => {
        sessionStorage.setItem(IS_FAVORITES_SESSION_STORAGE_KEY, isFavorites.toString());
    }, [isFavorites]);
    const tableData = useMemo(() => {
        const tableData = getTableData(rawData, filters, favorites, params);
        return tableData;
    }, [sort.field, sort.sortDirection, search, isFavorites, rawData, favorites, selectedIndustry, industriesType]);

    const favoritesAmount = tableData.Records.filter(({ isFavorite }) => isFavorite).length;
    const isFavoritesToggleDisabled = !isFavorites && favoritesAmount === 0;
    const table = useMemo(() => {
        if (isLoading) {
            return null;
        }
        const onFavoriteClick = onFavoriteClickHof(tableData, favorites, setFavorites, filters, setFilters);
        return (
            <SWReactTableOptimized
                type={"sticky"}
                tableData={tableData}
                tableColumns={tableColumns}
                onSort={(sort) => setFilters({ ...filters, sort })}
                tableOptions={{
                    onCellClick: (isOpen, rowIdx, rowData, columnConfig) => {
                        const { field } = columnConfig;
                        if (field === columns.favorite.field) {
                            onFavoriteClick(rowIdx);
                        }
                    },
                }}
            />
        );
    }, [filters, tableColumns, tableData, isLoading]);
    return (
        <Container>
            <TableTop
                filters={filters}
                setFilters={setFilters}
                favorites={favorites}
                isFavoritesToggleDisabled={isFavoritesToggleDisabled}
                favoritesAmount={favoritesAmount}
            />
            {isLoading ? <GhostLoader /> : table}
        </Container>
    );
};
